/**
 * ReMixChart - Optimized for Large Datasets
 * Split entry. See ./index.ts for public exports.
 */
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import ReactECharts from "echarts-for-react";
import {
  ChartArguements,
  EditableSeriesConfig,
  EditableSeriesPointChange,
  LegendKey,
  ReChartData,
} from "../../../models/chart_model";
import { useDispatch } from "react-redux";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import { compareLegends, legendsEqual } from "./utils/legendUtils";
import { LegendItem } from "./components/LegendComponent";
import { useBuildSeries } from "./hooks/useChartSeries";
import { useTooltipFormatter } from "./hooks/useTooltipFormatter";
import { useChartOptions } from "./hooks/useChartOptions";
import { useZoomState } from "./hooks/useZoomState";
import { useLegendDerivations } from "./hooks/useColorMap";
import { useEmptyStateVisibility } from "./hooks/useEmptyStateVisibility";
import { ChartClickParams } from "./types";
import "./ReMixChart.css";
import { EmptyState } from "./components/EmptyState";
import {
  MAX_COMPARE_POINTS,
  usePinCompareState,
} from "./hooks/usePinCompareState";
import { PinnedTooltipOverlay } from "./PinnedTooltipOverlay";
import { extractTooltipSnapshot } from "./utils/tooltipSnapshot";
import { useTooltipIdleGate } from "./hooks/useTooltipIdleGate";
import { useTooltipInteractionMode } from "./hooks/useTooltipInteractionMode";
import { CompareOnboardingOverlay } from "./CompareOnboardingOverlay";
import { useCriticalPeriods } from "./hooks/useCriticalPeriods";
import { CriticalPeriodsPanel } from "./components/CriticalPeriodsPanel";
import { CriticalBandLabels } from "./components/CriticalBandLabels";
import { CriticalPeriodKind, CriticalPeriodDef } from "../../../models/chart_model";

const DEFAULT_CRITICAL_PERIODS: CriticalPeriodDef[] = [
  { kind: "solar",        label: "Solar Hours",      startHour: 10, endHour: 16 },
  { kind: "evening_ramp", label: "Evening Peak",     startHour: 17, endHour: 21 },
  { kind: "valley",       label: "Night Low",        startHour: 0,  endHour: 5  },
];

const DOUBLE_CLICK_DELAY_MS = 220;
const CLICK_DRAG_THRESHOLD_PX = 6;
// Dwell time on a line before it surfaces as the Active Series. Filters out
// rapid mouseover churn while dragging the tooltip fast across many lines.
const ACTIVE_SERIES_DWELL_MS = 130;

export function ReMixChart({
  data,
  legends,
  onLegendSelect,
  unit = "",
  xDataKey,
  xLabel,
  yAxisLabel,
  legendFontSize,
  secondYAxisLabel,
  legendBreakIndex,
  hidePrice,
  circleLegend = false,
  xAxisLabelPosition = "insideBottom",
  xAxisLabelOffset,
  disableLegendClick = false,
  secondXDataKey,
  showBrush = true,
  brushIndex,
  labelFontSize,
  brushHeight = 17,
  xaxisHeight = 35,
  barCategoryGap = 2.5,
  xAxisTickMargin = 8,
  isTimeSlot,
  blockDurationMinutes,
  tooltipBoundary = "chart",
  hideTooltipOnChartExit = false,
  xAxisAngle,
  onBarClick,
  fontSize = 12,
  customTooltip,
  hiddenLegends = [],
  hiddenStats = [],
  enableMouseWheelZoom = true,
  chartContainerHeight = "",
  nameGap,
  showToolbox = false,
  yMaxValue,
  tooltipFilter,
  legendMaxHeight,
  isPriceForecast = false,
  defaultSelectedLegends = [],
  top = 30,
  bottom = 20,
  left,
  secondYAxisDomain,
  isSmallLegend = false,
  customLegendOrder,
  xAxisInterval,
  colorByPoint,
  barColors,
  highlightedXValues,
  enableClickSelection,
  editableSeries,
  isLoading,
  emptyStateDelayMs = 450,
  resolveDisplayName,
  resolveValue,
  enableDoubleClickCompare = true,
  showCriticalPeriods = false,
  criticalPeriods,
  xAxisTickFormatter,
}: ChartArguements) {
  const [selectedLegends, setSelectedLegends] = useState<LegendKey[]>(
    defaultSelectedLegends.length > 0 ? defaultSelectedLegends : legends,
  );

  // Resolved period list — explicit override or built-in defaults.
  const resolvedCriticalPeriods = useMemo<CriticalPeriodDef[]>(
    () => showCriticalPeriods
      ? (criticalPeriods && criticalPeriods.length > 0 ? criticalPeriods : DEFAULT_CRITICAL_PERIODS)
      : [],
    [showCriticalPeriods, criticalPeriods],
  );

  // Toggle state persisted in sessionStorage so filter-driven remounts don't reset it.
  const CP_STORAGE_KEY = "remix_cp_active_v2";
  const [activePeriodKinds, setActivePeriodKinds] = useState<Set<CriticalPeriodKind>>(() => {
    if (!showCriticalPeriods) return new Set<CriticalPeriodKind>();
    return new Set<CriticalPeriodKind>();
  });

  const handleToggleAllPeriods = useCallback(() => {
    setActivePeriodKinds((prev) => {
      const next = prev.size > 0 ? new Set<CriticalPeriodKind>() : new Set(resolvedCriticalPeriods.map(p => p.kind));
      try { sessionStorage.setItem(CP_STORAGE_KEY, next.size > 0 ? "on" : "off"); } catch { /* ignore */ }
      return next;
    });
  }, [resolvedCriticalPeriods]);

  // Bumped on datazoom to reposition HTML band labels. NOT bumped on
  // `finished` — that fires every render and would cause an infinite loop.
  const [bandLabelTick, setBandLabelTick] = useState(0);
  const bumpBandLabels = useCallback(() => setBandLabelTick(n => n + 1), []);

  const dispatch = useDispatch();
  const chartRef = useRef<ReactECharts>(null);
  const chartWrapRef = useRef<HTMLDivElement>(null);
  const dataRef = useRef<ReChartData[]>(data);
  const normalizeEditable = (
    e: EditableSeriesConfig | EditableSeriesConfig[] | undefined,
  ): EditableSeriesConfig[] =>
    e == null ? [] : Array.isArray(e) ? e.filter((c) => c?.enabled !== false) : [e];
  const editableSeriesRef = useRef<EditableSeriesConfig[]>(
    normalizeEditable(editableSeries),
  );
  const xDataKeyRef = useRef(xDataKey);
  const selectedLegendsRef = useRef<LegendKey[]>(selectedLegends);
  const editableFrameRef = useRef<number | null>(null);
  const syncEditableGraphicsRef = useRef<() => void>(() => {});
  const editableDragRef = useRef<{
    dataIndex: number;
    value: number;
    dataKey: string;
  } | null>(null);
  const dragTooltipStateRef = useRef<{
    active: boolean;
    dataIndex?: number;
    seriesName?: string;
    accentColor?: string;
  }>({ active: false });
  const activeSeriesRef = useRef<string | null>(null);
  const activeExitAnimRef = useRef<Animation | null>(null);
  const activeHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasEditableGraphicsRef = useRef(false);
  const [isCompact, setIsCompact] = useState(false);
  const prevLegendsRef = useRef<LegendKey[]>([]);
  const prevDefaultLegendsRef = useRef<LegendKey[]>([]);
  const compareSingleClickTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const comparePointerDownRef = useRef<{ x: number; y: number } | null>(null);
  const suppressCompareClickUntilRef = useRef(0);

  const isEmpty = !data || data.length === 0;
  const showEmpty = useEmptyStateVisibility({
    isEmpty,
    isLoading,
    delayMs: emptyStateDelayMs,
  });

  useEffect(() => {
    if (!isEmpty) return;
    const el = chartWrapRef.current;
    if (!el) return;
    const update = () => setIsCompact(el.clientHeight < 160);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isEmpty]);

  // Ensure ECharts canvas resizes when container size changes (e.g. on
  // tab switch back to Chart view after Summary/Table). Without this the
  // canvas can be initialised at 0×0 and never recover, leaving the
  // chart visually empty until window resize.
  useEffect(() => {
    const el = chartWrapRef.current;
    if (!el) return;
    let rafId: number | null = null;
    const triggerResize = () => {
      const instance = chartRef.current?.getEchartsInstance?.();
      if (!instance || instance.isDisposed?.()) return;
      const { clientWidth, clientHeight } = el;
      if (clientWidth > 0 && clientHeight > 0) {
        instance.resize({ width: clientWidth, height: clientHeight });
      }
    };
    const schedule = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        triggerResize();
      });
    };
    schedule();
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, []);

  const { lastZoom, zoomRange, handleDataZoom, consumeSkipFlag } = useZoomState(
    data,
    brushIndex?.startIndex,
    brushIndex?.endIndex,
    xDataKey,
  );

  // Gate React-driven option updates while user is actively dragging the
  // dataZoom slider. notMerge={true} causes every setOption to reinit the
  // chart; on heavy data that fights the user's drag and produces jitter.
  const isDraggingRef = useRef(false);
  const dragEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [, forceOptionFlush] = useState(0);
  const markDragging = useCallback(() => {
    isDraggingRef.current = true;
    if (dragEndTimerRef.current) clearTimeout(dragEndTimerRef.current);
    dragEndTimerRef.current = setTimeout(() => {
      isDraggingRef.current = false;
      dragEndTimerRef.current = null;
      forceOptionFlush((n) => n + 1);
    }, 200);
  }, []);
  useEffect(() => {
    return () => {
      if (dragEndTimerRef.current) clearTimeout(dragEndTimerRef.current);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (compareSingleClickTimerRef.current) {
        clearTimeout(compareSingleClickTimerRef.current);
        compareSingleClickTimerRef.current = null;
      }
    };
  }, []);

  // hideTip on unmount
  useEffect(() => {
    const chartInstance = chartRef.current?.getEchartsInstance?.();
    return () => {
      if (chartInstance) {
        chartInstance.dispatchAction({ type: "hideTip" });
      }
    };
  }, []);

  // Hide tooltip when data changes so ECharts does not try to re-evaluate
  // hover state on series that are mid-update.
  useEffect(() => {
    const chartInstance = chartRef.current?.getEchartsInstance?.();
    if (chartInstance && !chartInstance.isDisposed?.()) {
      chartInstance.dispatchAction({ type: "hideTip" });
    }
  }, [data]);

  // Hide tooltip when cursor is outside both the chart and the tooltip.
  // ECharts with appendTo:"body" puts the tooltip outside the chart DOM, so
  // a simple mouseleave on the wrapper isn't enough — we watch document
  // pointermove and hide when target is neither chart nor tooltip.
  useEffect(() => {
    const wrap = chartWrapRef.current;
    if (!wrap) return;
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    const hideNow = () => {
      const inst = chartRef.current?.getEchartsInstance?.();
      if (inst && !inst.isDisposed?.()) {
        inst.dispatchAction({ type: "hideTip" });
        const tooltipNodes = new Set<HTMLElement>();
        const uid = (inst as any)?.id ?? "";
        if (uid) {
          document
            .querySelectorAll(
              `.remix-tt .enhanced-tooltip[data-chart-uid="${uid}"]`,
            )
            .forEach((node) => {
              const root = (node as HTMLElement).closest(
                ".remix-tt",
              ) as HTMLElement | null;
              if (root) tooltipNodes.add(root);
            });
        }
        if (tooltipNodes.size === 0) {
          inst
            .getDom?.()
            ?.querySelectorAll?.(".remix-tt")
            ?.forEach((node: Element) => tooltipNodes.add(node as HTMLElement));
        }
        tooltipNodes.forEach((root) => {
          root.style.setProperty("display", "none", "important");
          root.style.setProperty("opacity", "0", "important");
          root.style.setProperty("visibility", "hidden", "important");
          root.style.setProperty("pointer-events", "none", "important");
          const host = root.closest(".echarts-tooltip") as HTMLElement | null;
          if (host) {
            host.style.setProperty("display", "none", "important");
            host.style.setProperty("opacity", "0", "important");
            host.style.setProperty("visibility", "hidden", "important");
            host.style.setProperty("pointer-events", "none", "important");
          }
        });
      }
    };
    const onMove = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      const overChart = !!t && (wrap === t || wrap.contains(t));
      const overTooltip = !!t?.closest?.(".remix-tt");
      const shouldKeepForTooltip = !hideTooltipOnChartExit && overTooltip;
      if (overChart || shouldKeepForTooltip) {
        if (hideTimer) {
          clearTimeout(hideTimer);
          hideTimer = null;
        }
        return;
      }
      if (hideTimer) return;
      hideTimer = setTimeout(() => {
        hideTimer = null;
        hideNow();
      }, 80);
    };
    const onWindowBlur = () => hideNow();
    const onWindowMouseOut = (e: MouseEvent) => {
      if (!e.relatedTarget) hideNow();
    };
    document.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("blur", onWindowBlur);
    window.addEventListener("mouseout", onWindowMouseOut);
    return () => {
      document.removeEventListener("pointermove", onMove);
      window.removeEventListener("blur", onWindowBlur);
      window.removeEventListener("mouseout", onWindowMouseOut);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [hideTooltipOnChartExit]);

  useEffect(() => {
    const newLegends =
      defaultSelectedLegends.length > 0 ? defaultSelectedLegends : legends;
    const prevDefaults = prevDefaultLegendsRef.current;
    const prevLeg = prevLegendsRef.current;
    if (!legendsEqual(newLegends, selectedLegends)) {
      if (
        !legendsEqual(defaultSelectedLegends, prevDefaults) ||
        !legendsEqual(legends, prevLeg)
      ) {
        setSelectedLegends(newLegends);
      }
    }
    prevDefaultLegendsRef.current = defaultSelectedLegends;
    prevLegendsRef.current = legends;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelectedLegends, legends]);

  const debouncedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data;
  }, [data]);

  useEffect(() => {
    dataRef.current = debouncedData;
  }, [debouncedData]);

  useEffect(() => {
    editableSeriesRef.current = normalizeEditable(editableSeries);
    if (editableSeriesRef.current.length > 0) {
      requestAnimationFrame(() => syncEditableGraphicsRef.current());
    }
  }, [editableSeries]);

  useEffect(() => {
    xDataKeyRef.current = xDataKey;
  }, [xDataKey]);

  useEffect(() => {
    selectedLegendsRef.current = selectedLegends;
  }, [selectedLegends]);

  const buildSeries = useBuildSeries();
  const getDragTooltipState = useCallback(
    () => dragTooltipStateRef.current,
    [],
  );
  const getActiveSeries = useCallback(() => activeSeriesRef.current, []);

  const syncActiveSeriesDOM = useCallback((seriesName: string | null) => {
    const inst = chartRef.current?.getEchartsInstance?.() as any;
    const uid = inst?.id ?? "";
    const card = uid
      ? (document.querySelector(
          `.remix-tt .enhanced-tooltip[data-chart-uid="${uid}"]`,
        ) as HTMLElement | null)
      : (inst
          ?.getDom?.()
          ?.querySelector(".remix-tt .enhanced-tooltip") as HTMLElement | null);
    if (!card) return;

    const otherContent = card.querySelector(".tooltip-content") as HTMLElement | null;
    if (!otherContent) return;
    let activeSection = card.querySelector(".tooltip-active-section") as HTMLElement | null;
    let otherLabel = card.querySelector(".tooltip-section-label--other") as HTMLElement | null;

    // Cancel any in-flight exit animation — we're re-entering before it finished.
    if (activeExitAnimRef.current) {
      activeExitAnimRef.current.cancel();
      activeExitAnimRef.current = null;
    }

    // Restore any currently-active item back into the Other list (in order).
    const demote = () => {
      const prev = card.querySelector(".tooltip-item--active") as HTMLElement | null;
      if (!prev) return;
      prev.classList.remove("tooltip-item--active");
      // Re-insert in sorted position by reading its original index attr if any,
      // else just prepend — order within Other list isn't required to be exact
      // for the demoted item, but prepend keeps it visible near the top.
      otherContent.appendChild(prev);
    };

    if (!seriesName) {
      if (!activeSection) {
        if (otherLabel) otherLabel.remove();
        return;
      }
      // Animate the section out, then demote + remove on completion.
      const sectionToClose = activeSection;
      const labelToRemove = otherLabel;
      const anim = sectionToClose.animate(
        [
          { opacity: 1, transform: "translateY(0)" },
          { opacity: 0, transform: "translateY(-4px)" },
        ],
        { duration: 160, easing: "cubic-bezier(0.4,0,1,1)", fill: "forwards" },
      );
      activeExitAnimRef.current = anim;
      anim.onfinish = () => {
        // Guard: a re-entry may have cleared/replaced this animation.
        if (activeExitAnimRef.current !== anim) return;
        activeExitAnimRef.current = null;
        demote();
        sectionToClose.remove();
        if (labelToRemove) labelToRemove.remove();
      };
      anim.oncancel = () => {
        // Re-entry cancelled us: keep the section, the new promote path handles it.
      };
      return;
    }

    const targetItem = Array.from(
      card.querySelectorAll(".tooltip-item"),
    ).find(
      (el) => (el as HTMLElement).getAttribute("data-series-name") === seriesName,
    ) as HTMLElement | null;
    if (!targetItem) return;

    // Already the active item — nothing to do.
    if (targetItem.classList.contains("tooltip-item--active")) return;

    demote();

    // Build the Active Series section just under the header if missing.
    // Animate the section in only on first creation (not on every X-position
    // re-render while hovering the same line) to avoid flicker.
    let sectionJustCreated = false;
    if (!activeSection) {
      sectionJustCreated = true;
      activeSection = document.createElement("div");
      activeSection.className = "tooltip-active-section";
      const label = document.createElement("div");
      label.className = "tooltip-section-label";
      label.textContent = "Active Series";
      activeSection.appendChild(label);
      // Insert before the Other content/label, i.e. before whichever comes first.
      const anchor = otherLabel || otherContent;
      card.insertBefore(activeSection, anchor);
    }

    // Ensure the "Other Series" label sits right before the Other list.
    if (!otherLabel) {
      otherLabel = document.createElement("div");
      otherLabel.className =
        "tooltip-section-label tooltip-section-label--other";
      otherLabel.textContent = "Other Series";
      card.insertBefore(otherLabel, otherContent);
    }

    targetItem.classList.add("tooltip-item--active");
    activeSection.appendChild(targetItem);

    if (sectionJustCreated) {
      activeSection.animate(
        [
          { opacity: 0, transform: "translateY(-4px)" },
          { opacity: 1, transform: "translateY(0)" },
        ],
        { duration: 200, easing: "cubic-bezier(0.22,1,0.36,1)" },
      );
    }
  }, []);

  useTooltipIdleGate({
    wrapRef: chartWrapRef,
    getChartUid: () =>
      (chartRef.current?.getEchartsInstance?.() as any)?.id ?? "",
  });

  const {
    state: pinState,
    actions: pinActions,
    latestHoverRef,
  } = usePinCompareState();

  useTooltipInteractionMode({
    wrapRef: chartWrapRef,
    getChartUid: () =>
      (chartRef.current?.getEchartsInstance?.() as any)?.id ?? "",
    pinState,
  });

  const [isCompareJustActivated, setIsCompareJustActivated] = useState(false);
  const prevIsCompareModeRef = useRef(false);
  
  useEffect(() => {
    if (pinState.isCompareMode && !prevIsCompareModeRef.current) {
      setIsCompareJustActivated(true);
      const t = setTimeout(() => setIsCompareJustActivated(false), 2000);
      return () => clearTimeout(t);
    }
    prevIsCompareModeRef.current = pinState.isCompareMode;
  }, [pinState.isCompareMode]);

  const cancelOnboarding = useCallback(() => {
    // Onboarding lives in its own component; nothing to cancel from parent.
    // Kept for downstream callsites (mouseout, datazoom).
  }, []);

  const handleLegendClick = useCallback(
    (legend: LegendKey) => {
      if (disableLegendClick) return;
      setSelectedLegends((prevSelectedLegends) => {
        const allSelected =
          prevSelectedLegends.length === legends.length ||
          prevSelectedLegends.length === 0;
        let newSelectedLegends;
        if (allSelected) {
          newSelectedLegends = [legend];
        } else {
          const exists = prevSelectedLegends.some(
            (item) => item.dataKey === legend.dataKey,
          );
          newSelectedLegends = exists
            ? prevSelectedLegends.filter(
                (item) => item.dataKey !== legend.dataKey,
              )
            : [...prevSelectedLegends, legend];
        }
        if (onLegendSelect) onLegendSelect(newSelectedLegends);
        return newSelectedLegends;
      });
    },
    [disableLegendClick, onLegendSelect, legends],
  );

  const { filteredLegends, colorMap, orderedSelectedLegends } =
    useLegendDerivations(legends, selectedLegends, xDataKey, customLegendOrder);

  const xAxisData = useMemo(
    () => debouncedData?.map((item) => item[xDataKey]) || [],
    [debouncedData, xDataKey],
  );

  const removeEditableGraphics = useCallback((chartInstance?: any) => {
    if (!hasEditableGraphicsRef.current) return;
    const instance = chartInstance ?? chartRef.current?.getEchartsInstance?.();
    if (!instance || instance.isDisposed?.()) {
      return;
    }
    instance.setOption({ graphic: [] }, {
      replaceMerge: ["graphic"],
      lazyUpdate: true,
    } as any);
    hasEditableGraphicsRef.current = false;
  }, []);

  const roundEditableValue = useCallback(
    (rawValue: number, config: EditableSeriesConfig) => {
      let nextValue = rawValue;
      if (typeof config.min === "number") {
        nextValue = Math.max(config.min, nextValue);
      }
      if (typeof config.max === "number") {
        nextValue = Math.min(config.max, nextValue);
      }
      if (typeof config.step === "number" && config.step > 0) {
        nextValue = Math.round(nextValue / config.step) * config.step;
      }
      const decimals =
        typeof config.step === "number" && String(config.step).includes(".")
          ? String(config.step).split(".")[1].length
          : 3;
      return Number(nextValue.toFixed(Math.min(decimals + 2, 8)));
    },
    [],
  );

  const buildEditablePayload = useCallback(
    (
      dataIndex: number,
      newValue: number,
      config: EditableSeriesConfig,
    ): EditableSeriesPointChange | null => {
      const rows = dataRef.current;
      const row = rows[dataIndex];
      if (!row) return null;
      const oldValue = Number(row[config.dataKey]);
      if (!Number.isFinite(oldValue)) return null;
      return {
        dataIndex,
        xValue: row[xDataKeyRef.current],
        oldValue,
        newValue,
        row,
      };
    },
    [],
  );

  const syncEditableGraphics = useCallback(() => {
    const chartInstance = chartRef.current?.getEchartsInstance?.();
    const configs = editableSeriesRef.current;
    const rows = dataRef.current;

    if (editableDragRef.current) return;
    if (
      !chartInstance ||
      chartInstance.isDisposed?.() ||
      configs.length === 0 ||
      rows.length === 0
    ) {
      removeEditableGraphics(chartInstance);
      return;
    }

    const option = chartInstance.getOption?.();

    const series = Array.isArray(option?.series) ? option.series : [];
    const xAxes = Array.isArray(option?.xAxis) ? option.xAxis : [];
    const yAxes = Array.isArray(option?.yAxis) ? option.yAxis : [];
    if (xAxes.length === 0 || yAxes.length === 0) return;
    // Guard: x-axis must have category data matching rows. Otherwise convertToPixel
    // collapses every dataIndex to grid-left and handles stack on the Y-axis.
    const xAxisCats = Array.isArray((xAxes[0] as any)?.data)
      ? (xAxes[0] as any).data
      : [];
    if (xAxisCats.length < rows.length) {
      removeEditableGraphics(chartInstance);
      return;
    }

    const selected = selectedLegendsRef.current;
    const activeConfigs = configs.filter((config) => {
      if (!config?.dataKey) return false;
      if (
        selected.length !== 0 &&
        !selected.some((legend) => legend.dataKey === config.dataKey)
      ) {
        return false;
      }
      return series.some(
        (item: any) =>
          item.id === config.dataKey || item.name === config.dataKey,
      );
    });

    if (activeConfigs.length === 0) {
      removeEditableGraphics(chartInstance);
      return;
    }

    // Compute plot area bounds to skip handles that would fall outside (e.g.
    // when convertToPixel returns origin for unrenderable points).
    const gridRect = (() => {
      try {
        const model = (chartInstance as any).getModel?.();
        const coordSys = model?.getComponent?.("grid")?.coordinateSystem;
        const rect = coordSys?.getRect?.();
        if (rect) return { x: rect.x, y: rect.y, w: rect.width, h: rect.height };
      } catch {}
      return null;
    })();

    const allGraphics: any[] = [];
    for (const config of activeConfigs) {
      const yAxisIndex = config.yAxisId === "right" ? 1 : 0;
      const configDataKey = config.dataKey;
      for (let dataIndex = 0; dataIndex < rows.length; dataIndex++) {
        const row = rows[dataIndex];
        const value = Number(row[configDataKey]);
        if (!Number.isFinite(value)) continue;

        const pixel = chartInstance.convertToPixel(
          { xAxisIndex: 0, yAxisIndex },
          [dataIndex, value],
        );
        if (
          !Array.isArray(pixel) ||
          !Number.isFinite(pixel[0]) ||
          !Number.isFinite(pixel[1])
        ) {
          continue;
        }
        // Skip handles outside plot area (prevents stragglers on Y-axis when
        // dataZoom hides a slot or convertToPixel snaps to grid edge).
        if (
          gridRect &&
          (pixel[0] < gridRect.x - 1 ||
            pixel[0] > gridRect.x + gridRect.w + 1 ||
            pixel[1] < gridRect.y - 1 ||
            pixel[1] > gridRect.y + gridRect.h + 1)
        ) {
          continue;
        }

        const handleSize = config.handleSize ?? 3;
        const capturedIndex = dataIndex;
        const capturedKey = configDataKey;

        allGraphics.push({
          type: "circle",
          x: pixel[0],
          y: pixel[1],
          shape: { r: handleSize },
          draggable: true,
          cursor: "ns-resize",
          z: 1000,
          style: {
            fill: config.handleColor ?? "#f1935c",
            stroke: "#ffffff",
            lineWidth: 1,
            opacity: 0.6,
          },
          onmousemove: () => {
            chartInstance.dispatchAction({
              type: "showTip",
              seriesId: capturedKey,
              dataIndex: capturedIndex,
            });
          },
          onmouseout: () => {
            chartInstance.dispatchAction({ type: "hideTip" });
          },
          ondrag: function (this: any) {
            const liveConfig = editableSeriesRef.current.find(
              (c) => c.dataKey === capturedKey,
            );
            if (!liveConfig) return;
            const currentRows = dataRef.current;
            const currentRow = currentRows[capturedIndex];
            if (!currentRow) return;

            const currentValue = Number(currentRow[capturedKey]);
            if (!Number.isFinite(currentValue)) return;
            const currentPixel = chartInstance.convertToPixel(
              { xAxisIndex: 0, yAxisIndex },
              [capturedIndex, currentValue],
            );
            const converted = chartInstance.convertFromPixel(
              { xAxisIndex: 0, yAxisIndex },
              [currentPixel[0], this.y],
            );
            const rawNextValue = Number(converted?.[1]);
            if (!Number.isFinite(rawNextValue)) return;

            const newValue = roundEditableValue(rawNextValue, liveConfig);
            const snappedPixel = chartInstance.convertToPixel(
              { xAxisIndex: 0, yAxisIndex },
              [capturedIndex, newValue],
            );
            this.x = currentPixel[0];
            if (
              Array.isArray(snappedPixel) &&
              Number.isFinite(snappedPixel[1])
            ) {
              this.y = snappedPixel[1];
            }

            if (Math.abs(newValue - currentValue) < 0.000001) return;

            editableDragRef.current = {
              dataIndex: capturedIndex,
              value: newValue,
              dataKey: capturedKey,
            };
            const previewData = currentRows.map((previewRow, previewIndex) =>
              previewIndex === capturedIndex
                ? newValue
                : (previewRow[capturedKey] ?? undefined),
            );
            chartInstance.setOption(
              {
                series: [{ id: capturedKey, data: previewData }],
              },
              {
                lazyUpdate: true,
              },
            );
            chartInstance.dispatchAction({ type: "hideTip" });
            chartInstance.dispatchAction({
              type: "showTip",
              seriesId: capturedKey,
              dataIndex: capturedIndex,
              position: [currentPixel[0], this.y],
            });
          },
          ondragend: function () {
            const liveConfig = editableSeriesRef.current.find(
              (c) => c.dataKey === capturedKey,
            );
            const dragged = editableDragRef.current;
            editableDragRef.current = null;
            if (
              !liveConfig ||
              !dragged ||
              dragged.dataIndex !== capturedIndex ||
              dragged.dataKey !== capturedKey
            ) {
              requestAnimationFrame(() => syncEditableGraphicsRef.current());
              return;
            }
            const payload = buildEditablePayload(
              capturedIndex,
              dragged.value,
              liveConfig,
            );
            if (payload) {
              liveConfig.onPointChange(payload);
              liveConfig.onPointChangeEnd?.(payload);
            }
            requestAnimationFrame(() => syncEditableGraphicsRef.current());
          },
        });
      }
    }

    chartInstance.setOption({ graphic: allGraphics }, {
      replaceMerge: ["graphic"],
      lazyUpdate: true,
    } as any);
    hasEditableGraphicsRef.current = true;
  }, [buildEditablePayload, removeEditableGraphics, roundEditableValue]);

  useEffect(() => {
    syncEditableGraphicsRef.current = syncEditableGraphics;
  }, [syncEditableGraphics]);

  const hasAnyEditable = normalizeEditable(editableSeries).length > 0;

  const scheduleEditableGraphicSync = useCallback(() => {
    if (editableSeriesRef.current.length === 0) return;
    if (editableFrameRef.current !== null) {
      cancelAnimationFrame(editableFrameRef.current);
    }
    editableFrameRef.current = requestAnimationFrame(() => {
      editableFrameRef.current = null;
      syncEditableGraphics();
    });
  }, [syncEditableGraphics]);

  useEffect(() => {
    if (hasAnyEditable) return;
    editableDragRef.current = null;
    removeEditableGraphics();
  }, [hasAnyEditable, removeEditableGraphics]);

  useEffect(() => {
    const el = chartWrapRef.current;
    if (!el || !hasAnyEditable) return;
    const ro = new ResizeObserver(scheduleEditableGraphicSync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasAnyEditable, scheduleEditableGraphicSync]);

  useEffect(
    () => () => {
      if (editableFrameRef.current !== null) {
        cancelAnimationFrame(editableFrameRef.current);
      }
      if (activeHoverTimerRef.current) {
        clearTimeout(activeHoverTimerRef.current);
      }
      removeEditableGraphics();
    },
    [removeEditableGraphics],
  );

  const tooltipFormatter = useTooltipFormatter({
    tooltipFilter,
    secondXDataKey,
    debouncedData,
    isTimeSlot,
    blockDurationMinutes,
    fontSize,
    isPriceForecast,
    legends,
    hidePrice,
    unit,
    customLegendOrder,
    getDragTooltipState,
    getActiveSeries,
    getChartInstance: () => chartRef.current?.getEchartsInstance?.(),
    pinState,
    pinActions,
    latestHoverRef,
    resolveDisplayName,
    resolveValue,
    enableDoubleClickCompare,
  });

  // Keep tooltip enabled so hover snapshot collection works in all modes.
  useEffect(() => {
    // Clear any stale active-series state on compare-mode transitions.
    activeSeriesRef.current = null;
    const inst = chartRef.current?.getEchartsInstance?.();
    if (!inst || inst.isDisposed?.()) return;
    inst.setOption({ tooltip: { show: true } }, { lazyUpdate: true });
    const opt = inst.getOption?.();
    const series = Array.isArray(opt?.series) ? opt.series : [];
    const namedSeries = series.filter(
      (s: any) => s?.name && !String(s.name).startsWith("__"),
    );
    if (namedSeries.length > 0) {
      inst.dispatchAction({
        type: "downplay",
        seriesName: namedSeries.map((s: any) => s.name),
      });
    }
    const uid = (inst as any)?.id ?? "";
    const r =
      (uid &&
        (document
          .querySelector(`.remix-tt .enhanced-tooltip[data-chart-uid="${uid}"]`)
          ?.closest(".remix-tt") as HTMLElement | null)) ||
      (inst.getDom?.()?.querySelector(".remix-tt") as HTMLElement | null);
    if (r) r.setAttribute("data-focus", "0");
  }, [pinState.isCompareMode]);

  // If data shape changes, clear compare mode when a selected point is no longer valid.
  useEffect(() => {
    if (!pinState.isCompareMode) return;
    const hasInvalidPoint = pinState.comparePoints.some(
      (point) => point.dataIndex >= debouncedData.length,
    );
    if (hasInvalidPoint) {
      pinActions.clearCompareMode();
    }
  }, [
    debouncedData,
    pinState.comparePoints,
    pinState.isCompareMode,
    pinActions,
  ]);

  const buildSnapForIndex = useCallback(
    (dataIndex: number) => {
      const inst = chartRef.current?.getEchartsInstance?.();
      if (!inst || inst.isDisposed?.()) return null;
      const option = inst.getOption();
      const series = Array.isArray(option?.series) ? option.series : [];
      if (!series.length) return null;

      const params = series
        .map((s: any, idx: number) => {
          let p;
          if (typeof (inst as any).getDataParams === "function") {
            try {
              p = (inst as any).getDataParams(idx, dataIndex);
            } catch (err) {
              p = null;
            }
          }
          if (!p) {
            p = {
              seriesName: s.name,
              seriesId: s.id,
              name: debouncedData[dataIndex]?.[xDataKeyRef.current],
              dataIndex,
              value: debouncedData[dataIndex]?.[s.name || s.id],
              color: colorMap[s.name] || s.itemStyle?.color,
            };
          }
          return p;
        })
        .filter(Boolean);

      return extractTooltipSnapshot(params, {
        legends,
        unit,
        hidePrice,
        isTimeSlot,
        isPriceForecast,
        secondXDataKey,
        debouncedData,
        customLegendOrder,
        tooltipFilter,
      });
    },
    [
      debouncedData,
      legends,
      unit,
      hidePrice,
      isTimeSlot,
      isPriceForecast,
      secondXDataKey,
      customLegendOrder,
      tooltipFilter,
      colorMap,
    ],
  );

  useEffect(() => {
    const inst = chartRef.current?.getEchartsInstance?.();
    if (!inst || inst.isDisposed?.()) return;
    const dom = inst.getDom?.() as HTMLElement | null;
    if (!dom) return;

    const clearPendingSingleClick = () => {
      if (compareSingleClickTimerRef.current) {
        clearTimeout(compareSingleClickTimerRef.current);
        compareSingleClickTimerRef.current = null;
      }
    };

    const resolveSnapFromClientPoint = (clientX: number, clientY: number) => {
      const rect = dom.getBoundingClientRect();
      const offsetX = clientX - rect.left;
      const offsetY = clientY - rect.top;
      const pointInPixel: [number, number] = [offsetX, offsetY];

      let dataIndex = latestHoverRef.current?.dataIndex ?? -1;
      const finders = [{ seriesIndex: 0 }, { xAxisIndex: 0 }, { gridIndex: 0 }];
      for (const finder of finders) {
        if (inst.containPixel(finder as any, pointInPixel)) {
          const pt = inst.convertFromPixel(finder as any, pointInPixel);
          if (pt && pt.length > 0) {
            dataIndex = Math.round(Number(pt[0]));
            break;
          }
        }
      }
      if (dataIndex < 0 || dataIndex >= debouncedData.length) return null;
      const hovered = latestHoverRef.current;
      if (hovered && hovered.dataIndex === dataIndex) return hovered;
      return buildSnapForIndex(dataIndex);
    };

    const isIgnoredTarget = (target: EventTarget | null) => {
      const node = target as HTMLElement | null;
      if (!node) return false;
      if (node.closest?.(".remix-pin-overlay")) return true;
      if (node.closest?.(".remix-tt")) return true;
      return false;
    };

    const onPointerDown = (event: PointerEvent) => {
      if (isIgnoredTarget(event.target)) return;
      comparePointerDownRef.current = { x: event.clientX, y: event.clientY };
    };

    const onPointerUp = (event: PointerEvent) => {
      const start = comparePointerDownRef.current;
      comparePointerDownRef.current = null;
      if (!start) return;
      const moved = Math.hypot(
        event.clientX - start.x,
        event.clientY - start.y,
      );
      if (moved > CLICK_DRAG_THRESHOLD_PX || isDraggingRef.current) {
        suppressCompareClickUntilRef.current = performance.now() + 260;
      }
    };

    const onClick = (event: MouseEvent) => {
      if (isIgnoredTarget(event.target)) return;
      if (event.detail > 1) return;
      if (performance.now() < suppressCompareClickUntilRef.current) return;
      if (!pinState.isCompareMode) return;

      clearPendingSingleClick();
      const clickX = event.clientX;
      const clickY = event.clientY;
      compareSingleClickTimerRef.current = setTimeout(() => {
        compareSingleClickTimerRef.current = null;
        const snap = resolveSnapFromClientPoint(clickX, clickY);
        if (!snap) return;
        pinActions.toggleComparePoint(snap);
      }, DOUBLE_CLICK_DELAY_MS);
    };

    const onDoubleClick = (event: MouseEvent) => {
      if (!enableDoubleClickCompare) return;
      if (isIgnoredTarget(event.target)) return;
      if (performance.now() < suppressCompareClickUntilRef.current) return;
      clearPendingSingleClick();
      const snap = resolveSnapFromClientPoint(event.clientX, event.clientY);
      if (!snap) return;
      if (!pinState.isCompareMode) {
        pinActions.enterCompareMode(snap);
      } else {
        pinActions.setReferencePoint(snap);
      }
      event.preventDefault();
      event.stopPropagation();
    };

    dom.addEventListener("pointerdown", onPointerDown);
    dom.addEventListener("pointerup", onPointerUp);
    dom.addEventListener("click", onClick);
    dom.addEventListener("dblclick", onDoubleClick);

    return () => {
      clearPendingSingleClick();
      dom.removeEventListener("pointerdown", onPointerDown);
      dom.removeEventListener("pointerup", onPointerUp);
      dom.removeEventListener("click", onClick);
      dom.removeEventListener("dblclick", onDoubleClick);
    };
  }, [
    pinState.isCompareMode,
    debouncedData,
    buildSnapForIndex,
    pinActions,
    latestHoverRef,
    enableDoubleClickCompare,
  ]);

  // Keyboard flow: Enter toggles compare point, R sets reference, Esc clears,
  // Arrow keys move hover focus.
  useEffect(() => {
    const el = chartWrapRef.current;
    if (!el) return;
    const onEnter = () => {
      if (document.activeElement !== el) {
        try {
          el.focus({ preventScroll: true });
        } catch {
          el.focus();
        }
      }
    };
    el.addEventListener("mouseenter", onEnter);
    return () => el.removeEventListener("mouseenter", onEnter);
  }, []);

  useEffect(() => {
    const el = chartWrapRef.current;
    if (!el) return;

    const isEditableTarget = (t: EventTarget | null) => {
      const node = t as HTMLElement | null;
      if (!node) return false;
      if (node === el) return false;
      const tag = node.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        (node as HTMLElement).isContentEditable
      ) {
        return true;
      }
      return false;
    };

    const onKey = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return;
      const inst = chartRef.current?.getEchartsInstance?.();
      if (!inst || inst.isDisposed?.()) return;

      const key = e.key;

      if (key === "Escape") {
        if (pinState.isCompareMode) {
          pinActions.clearCompareMode();
          inst.dispatchAction({ type: "hideTip" });
          e.preventDefault();
        }
        return;
      }

      if (key === "Enter") {
        const snap = latestHoverRef.current;
        if (!snap) return;
        if (!pinState.isCompareMode) {
          pinActions.enterCompareMode(snap);
        } else {
          pinActions.toggleComparePoint(snap);
        }
        e.preventDefault();
        return;
      }

      if (key === "r" || key === "R") {
        if (!pinState.isCompareMode) return;
        const snap = latestHoverRef.current;
        if (!snap) return;
        pinActions.setReferencePoint(snap);
        e.preventDefault();
        return;
      }

      if (key === "ArrowLeft" || key === "ArrowRight") {
        const len = debouncedData.length;
        if (!len) return;
        const baseIdx =
          latestHoverRef.current?.dataIndex ?? pinState.referenceDataIndex ?? 0;
        const step = key === "ArrowLeft" ? -1 : 1;
        const next = Math.max(0, Math.min(len - 1, baseIdx + step));
        if (next === baseIdx) return;
        inst.dispatchAction({
          type: "showTip",
          seriesIndex: 0,
          dataIndex: next,
        });
        if (pinState.isCompareMode) {
          const snap = buildSnapForIndex(next);
          if (snap) pinActions.setHoverPoint(snap);
        }
        e.preventDefault();
      }
    };

    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [
    pinState.isCompareMode,
    pinState.referenceDataIndex,
    pinActions,
    latestHoverRef,
    debouncedData,
    buildSnapForIndex,
  ]);

  const { bands: criticalPeriodBands, supported: criticalSupported } = useCriticalPeriods(
    resolvedCriticalPeriods,
    xAxisData as (string | number)[],
    activePeriodKinds,
  );

  const stableOptionRef = useRef<any>(null);
  const computedOptions = useChartOptions({
    debouncedData,
    orderedSelectedLegends,
    selectedLegends,
    hiddenStats,
    colorMap,
    barCategoryGap,
    isPriceForecast,
    left,
    showBrush,
    brushHeight,
    xaxisHeight,
    isSmallLegend,
    bottom,
    top,
    customTooltip,
    tooltipFormatter,
    fontSize,
    showToolbox,
    xAxisData,
    xAxisAngle,
    xAxisTickMargin,
    xLabel,
    xAxisLabelPosition,
    xAxisLabelOffset,
    labelFontSize,
    secondXDataKey,
    yMaxValue,
    yAxisLabel,
    nameGap,
    secondYAxisLabel,
    brushIndex,
    enableMouseWheelZoom,
    secondYAxisDomain,
    buildSeries,
    colorByPoint,
    barColors,
    enableClickSelection,
    highlightedXValues,
    xDataKey,
    xAxisInterval,
    isTimeSlot,
    hideTooltipOnChartExit,
    tooltipBoundary,
    zoomRange,
    lastZoomRef: lastZoom,
    getChartRect: () => chartWrapRef.current?.getBoundingClientRect?.() ?? null,
    criticalPeriodBands,
    xAxisTickFormatter,
  });

  // During an active drag, hand ECharts the previously-rendered option ref so
  // React's notMerge={true} setOption doesn't reinit the chart mid-drag.
  const chartOptions =
    isDraggingRef.current && stableOptionRef.current
      ? stableOptionRef.current
      : computedOptions;
  useEffect(() => {
    if (!isDraggingRef.current) {
      stableOptionRef.current = computedOptions;
    }
  }, [computedOptions]);

  const onChartEvents = useMemo(
    () => ({
      click: (params: ChartClickParams) => {
        if (pinState.isCompareMode) return;
        if (params.seriesName?.startsWith("__")) {
          if (onBarClick) {
            const clickedData = debouncedData[params.dataIndex ?? -1];
            if (clickedData) {
              onBarClick({
                ...params,
                name: clickedData[xDataKey],
                seriesName: "user_click",
                data: clickedData,
              });
            }
          }
        } else if (onBarClick) {
          onBarClick(params);
        }
      },
      mouseover: (params: any) => {
        // Active-series surfacing is a single-hover affordance; skip it in
        // compare mode where the tooltip is a multi-point pinned snapshot.
        if (pinState.isCompareMode) return;
        if (params.seriesName && !params.seriesName.startsWith("__")) {
          const name = params.seriesName;
          // Already showing this series — no work, no reschedule.
          if (activeSeriesRef.current === name) return;
          // Debounce: require a brief dwell on the line before promoting, so
          // a fast drag across many lines doesn't thrash the Active section.
          if (activeHoverTimerRef.current) clearTimeout(activeHoverTimerRef.current);
          activeHoverTimerRef.current = setTimeout(() => {
            activeHoverTimerRef.current = null;
            activeSeriesRef.current = name;
            requestAnimationFrame(() => syncActiveSeriesDOM(name));
          }, ACTIVE_SERIES_DWELL_MS);
        }
      },
      mouseout: () => {
        if (pinState.isCompareMode) return;
        if (activeHoverTimerRef.current) {
          clearTimeout(activeHoverTimerRef.current);
          activeHoverTimerRef.current = null;
        }
        if (activeSeriesRef.current === null) return;
        activeSeriesRef.current = null;
        requestAnimationFrame(() => syncActiveSeriesDOM(null));
        cancelOnboarding();
      },
      datazoom: (params: any) => {
        if (consumeSkipFlag()) return;
        markDragging();
        let start: number | undefined;
        let end: number | undefined;
        const zoom = params.batch ? params.batch[0] : params;
        if (zoom.start !== undefined && zoom.end !== undefined) {
          start = zoom.start;
          end = zoom.end;
        } else if (chartRef.current) {
          const chartInstance = chartRef.current.getEchartsInstance();
          const option = chartInstance.getOption();
          const dz = option.dataZoom as any[];
          if (dz && dz.length > 0) {
            start = dz[0].start;
            end = dz[0].end;
          }
        }
        if (start !== undefined && end !== undefined) {
          handleDataZoom(start, end);
          scheduleEditableGraphicSync();
        }
        bumpBandLabels();
        cancelOnboarding();
      },
      finished: () => {
        requestAnimationFrame(() => dispatch(setLoadingFilterName("")));
        scheduleEditableGraphicSync();
      },
      globalout: () => {
        activeSeriesRef.current = null;
        const inst = chartRef.current?.getEchartsInstance?.();
        if (hideTooltipOnChartExit && inst && !inst.isDisposed?.()) {
          inst.dispatchAction({ type: "hideTip" });
        }
        const uid = (inst as any)?.id ?? "";
        const r: any =
          (uid &&
            document
              .querySelector(
                `.remix-tt .enhanced-tooltip[data-chart-uid="${uid}"]`,
              )
              ?.closest(".remix-tt")) ||
          inst?.getDom?.()?.querySelector(".remix-tt");
        if (r?.__resetHover) r.__resetHover();
      },
    }),
    [
      onBarClick,
      debouncedData,
      xDataKey,
      dispatch,
      consumeSkipFlag,
      handleDataZoom,
      scheduleEditableGraphicSync,
      pinState.isCompareMode,
      markDragging,
      hideTooltipOnChartExit,
      cancelOnboarding,
      syncActiveSeriesDOM,
      bumpBandLabels,
    ],
  );

  const containerStyle = useMemo(
    () =>
      ({
        height: chartContainerHeight,
        ["--remix-fontSize" as any]: `${fontSize}px`,
        ["--remix-legendFontSize" as any]: `${legendFontSize ?? fontSize}px`,
      }) as React.CSSProperties,
    [chartContainerHeight, fontSize, legendFontSize],
  );

  return (
    <>
      <div
        style={containerStyle}
        className="chart-container tab-animatable-content remix-tooltip-vars"
      >
        <div
          ref={chartWrapRef}
          className="remix-chart-wrap group relative h-full w-full overflow-hidden"
          data-compact={isCompact ? "true" : "false"}
          data-compare-mode={pinState.isCompareMode ? "true" : "false"}
          tabIndex={0}
          role="application"
          aria-label="Interactive chart. Hover to inspect. Double-click any point to start comparing, click to toggle points in compare mode, Esc to clear, Arrow keys to navigate."
        >
          <ReactECharts
            ref={chartRef}
            option={chartOptions}
            style={{
              height: "100%",
              width: "100%",
              visibility: isEmpty && showEmpty ? "hidden" : "visible",
            }}
            opts={{ renderer: "canvas" }}
            onEvents={onChartEvents}
            notMerge={true}
          />
          {/* Critical Periods — toggle button + animated HTML band labels */}
          {resolvedCriticalPeriods.length > 0 && criticalSupported && (
            <CriticalPeriodsPanel
              periods={resolvedCriticalPeriods}
              activePeriodKinds={activePeriodKinds}
              onToggleAll={handleToggleAllPeriods}
            />
          )}
          {criticalSupported && criticalPeriodBands.length > 0 && (
            <CriticalBandLabels
              bands={criticalPeriodBands}
              chartRef={chartRef}
              repositionTick={bandLabelTick}
            />
          )}
          {pinState.isCompareMode && (
            <div className={`remix-compare-status-chip ${isCompareJustActivated ? "remix-compare-status-toast" : ""}`} aria-live="polite">
              {isCompareJustActivated ? (
                <>
                  <span className="remix-compare-toast-check">✓</span> Compare Mode Active
                  <span className="remix-compare-toast-sub">Click points to add · Esc to clear</span>
                </>
              ) : (
                `COMPARE • ${pinState.comparePoints.length}/${MAX_COMPARE_POINTS}`
              )}
            </div>
          )}
          <CompareOnboardingOverlay
            isCompareModeActive={pinState.isCompareMode}
            enabled={enableDoubleClickCompare !== false}
            chartWrapRef={chartWrapRef}
            chartRef={chartRef}
            latestHoverRef={latestHoverRef}
          />
          {isEmpty && showEmpty && <EmptyState compact={isCompact} />}
          <PinnedTooltipOverlay
            state={pinState}
            actions={pinActions}
            containerEl={chartWrapRef.current}
            customLegendOrder={customLegendOrder}
            fontSize={fontSize}
          />
        </div>
      </div>

      <div
        style={{
          maxHeight: legendMaxHeight || "17%",
          display: isEmpty ? "none" : undefined,
        }}
        className={`flex flex-wrap justify-center gap-x-2 overflow-legend tab-animatable-content remix-tooltip-vars ${
          showBrush && !isSmallLegend && "pt-1"
        }`}
      >
        {filteredLegends
          .slice()
          .sort((a, b) =>
            compareLegends(a.name || "", b.name || "", customLegendOrder),
          )
          .map((legend, index) => {
            const isHidden = hiddenLegends.includes(legend.name ?? "");
            return (
              <React.Fragment key={index}>
                <div className={`mb-3 ${isHidden ? "hidden" : ""}`}>
                  <LegendItem
                    legend={legend}
                    index={index}
                    selectedLegends={selectedLegends}
                    handleLegendClick={handleLegendClick}
                    circleOnly={circleLegend}
                    fontSize={legendFontSize}
                    colorMap={colorMap}
                    isSmallLegend={isSmallLegend}
                  />
                </div>
                {legendBreakIndex != 0 && legendBreakIndex === index && (
                  <div style={{ width: "100%", height: "2px" }}></div>
                )}
              </React.Fragment>
            );
          })}
      </div>
    </>
  );
}
